import AddressAutocomplete, { type IParsedAddress } from '@orn/shared-web/components/AddressAutocomplete';
import Input from '@orn/shared-web/components/Input/Input';
import Label from '@orn/shared-web/components/Input/Label';
import Select, { type SelectItemType } from '@orn/shared-web/components/Select/Select';
import SelectItem from '@orn/shared-web/components/Select/SelectItem';
import YesNoToggle from '@orn/shared-web/components/YesNoToggle';
import { CloseIcon } from '@orn/shared-web/icons';
import logger from '@orn/shared-web/lib/logger';
import * as React from 'react';

import { HousingOwnership, ResidenceType, type UpsertResidenceInput } from '../../../__generated__/globalTypes';
import { useDeleteResidence } from '../../../hooks/mutations/useDeleteResidence';
import { useUpdateCaregiverRelationship } from '../../../hooks/mutations/useUpdateCaregiverRelationship';
import { useUpdateDependantPartnership } from '../../../hooks/mutations/useUpdateDependantPartnership';
import { useUpsertResidence } from '../../../hooks/mutations/useUpsertResidence';
import {
  useOnboardingDependantResidences,
  type IDependantResidences,
  type IOnboardingResidence,
} from '../../../hooks/queries/useOnboardingDependantResidences';
import { useOnboardingDependants } from '../../../hooks/queries/useOnboardingDependants';
import { getOptionalEnv } from '../../../lib/getEnv';
import { OnboardingTitle } from '../../OnboardingLayout';

import { getDependantNames, getDependantPossessive, getDisplayName } from './dependantUtils';
import OnboardingStepActions from './OnboardingStepActions';

import type { IOnboardingStepProps } from '../types';

interface IResidenceTypeOption {
  id: ResidenceType;
  label: string;
}

const RESIDENCE_TYPE_OPTIONS: IResidenceTypeOption[] = [
  { id: ResidenceType.IndependentLiving, label: 'Living independently (at home)' },
  { id: ResidenceType.FamilyMember, label: 'Living with family member' },
  { id: ResidenceType.AssistedLiving, label: 'Living in nursing home or assisted living facility' },
  { id: ResidenceType.Other, label: 'Other' },
];

interface IHomeOwnershipOption {
  id: HousingOwnership;
  label: string;
}

const HOME_OWNERSHIP_OPTIONS: IHomeOwnershipOption[] = [
  { id: HousingOwnership.OwnOutright, label: 'Own outright' },
  { id: HousingOwnership.Mortgage, label: 'Own with mortgage' },
  { id: HousingOwnership.Rent, label: 'Rent' },
  { id: HousingOwnership.Other, label: 'Other' },
];

// ---------------------------------------------------------------------------
// Sub-form state for a single "parent group" — used twice in Path B (one
// state object per parent) and once in Path A (one state object shared
// across all dependants in the same household).
// ---------------------------------------------------------------------------

/**
 * Structured address picked from the Places typeahead (or hydrated from an
 * existing `Residence`), flattened to the `upsertResidence` columns.
 * `addressLine1` is `null` for non-street picks (a city/region like
 * "Truckee, CA") — primary residences gate on it being present; frequent
 * locations do not.
 */
export interface IResidenceAddress {
  formatted: string;
  addressLine1: string | null;
  addressLine2: string | null;
  city: string;
  state: string | null;
  postalCode: string | null;
  country: string;
  placeId: string | null;
}

/**
 * A picked address is submittable only when both NOT-NULL residence columns
 * are present. Google returns plenty of legitimate picks (a region, a POI, a
 * non-locality place) with no city component, which `toResidenceAddress`
 * flattens to `''` — that empty value would fail `_assertNonEmptyText` on the
 * wire, so the Continue gate must reject it here rather than light up and let
 * the backend 500.
 */
export const isCompleteAddress = (address: IResidenceAddress | null): address is IResidenceAddress =>
  address !== null && address.city.trim().length > 0 && address.country.trim().length > 0;

/** Map a Google Places result into our flat residence-address shape. */
const toResidenceAddress = (p: IParsedAddress): IResidenceAddress => ({
  formatted: p.formattedAddress,
  addressLine1: p.streetNumber && p.streetName ? `${p.streetNumber} ${p.streetName}` : null,
  addressLine2: p.subpremise ?? null,
  city: p.city ?? '',
  state: p.state ?? null,
  postalCode: p.postalCode ?? null,
  country: p.country ?? '',
  placeId: p.placeId || null,
});

/** Map an existing persisted residence into the form's address shape. */
const residenceToAddress = (r: IOnboardingResidence): IResidenceAddress => ({
  formatted: r.formatted ?? r.addressLine1 ?? r.city,
  addressLine1: r.addressLine1 ?? null,
  addressLine2: r.addressLine2 ?? null,
  city: r.city,
  state: r.state ?? null,
  postalCode: r.postalCode ?? null,
  country: r.country,
  placeId: r.placeId ?? null,
});

export interface IFrequentPlace {
  /** `Residence.id` of the existing row to upsert into, or `null` for new entries. */
  id: string | null;
  /** Controlled input text. */
  text: string;
  /** Structured pick — `null` until the user selects a suggestion. */
  address: IResidenceAddress | null;
}

export interface IResidenceFormState {
  /** Existing primary `Residence.id` for this group, or `null` if none yet. Threaded through to `upsertResidence` so a re-submit updates the row. */
  primaryResidenceId: string | null;
  liveWithCaregiver: boolean | null;
  /** Controlled address-input text. */
  addressText: string;
  /** Structured pick — `null` until the user selects a full-address suggestion. */
  address: IResidenceAddress | null;
  residenceType: ResidenceType;
  residenceTypeOther: string;
  homeOwnership: HousingOwnership | null;
  homeOwnershipOther: string;
}

/**
 * Shared "frequently spend time elsewhere" state — lifted out of the
 * per-parent form so Path B (parents living apart) still gets a single
 * aggregated list rather than asking the question twice. Persisted as
 * family-shared `Residence` rows (owner null) — the question is asked once
 * across the household, so the rows are never pinned to an individual
 * dependant.
 *
 * Removals are NOT tracked here — the step computes a real diff against the
 * hydration snapshot at submit time, so anything that started in the DB and
 * isn't in `places` (or the toggle is `false`) gets a `deleteResidence`
 * call automatically. Same applies to flipped `liveWithCaregiver` answers
 * and Path A↔B switches.
 */
export interface IFrequentLocationsState {
  toggle: boolean | null;
  /** Free-text addresses, one per row; first row is rendered as soon as the toggle flips on. */
  places: IFrequentPlace[];
}

const INITIAL_FORM_STATE: IResidenceFormState = {
  primaryResidenceId: null,
  liveWithCaregiver: null,
  addressText: '',
  address: null,
  residenceType: ResidenceType.IndependentLiving,
  residenceTypeOther: '',
  homeOwnership: null,
  homeOwnershipOther: '',
};

const INITIAL_FREQUENT_LOCATIONS_STATE: IFrequentLocationsState = {
  toggle: null,
  places: [{ id: null, text: '', address: null }],
};

/**
 * Pre-fill a single parent group's form from existing data. The
 * `liveWithCaregiver` answer is read from the persisted
 * `caregiver_relationships.lives_with_caregiver` flag — NOT inferred from
 * residence presence, because a "lives with you = Yes" answer intentionally
 * creates no residence row.
 *
 * The address/living-situation fields are only adopted when the answer is an
 * explicit `false` AND the resolved primary is this dependant's OWN residence.
 * The server resolves a dependant's `primary` through cohabitant/caregiver
 * chains (see ResidenceController), so a primary owned by the caregiver or a
 * partner must never be written back as this dependant's own residence —
 * doing so would reparent another member's row on submit.
 */
const hydrateFormState = (
  dependantId: string,
  livesWithCaregiver: boolean | null,
  dependantResidence: IDependantResidences | null
): IResidenceFormState => {
  const primary = dependantResidence?.primary ?? null;
  const ownsPrimary = primary !== null && primary.ownerKind === 'dependant' && primary.ownerId === dependantId;
  if (livesWithCaregiver === false && ownsPrimary) {
    return {
      primaryResidenceId: primary.id,
      liveWithCaregiver: false,
      addressText: primary.formatted ?? primary.addressLine1 ?? '',
      address: residenceToAddress(primary),
      residenceType: primary.residenceType,
      residenceTypeOther: primary.residenceTypeOther ?? '',
      homeOwnership: primary.ownership ?? null,
      homeOwnershipOther: primary.ownershipOther ?? '',
    };
  }
  return { ...INITIAL_FORM_STATE, liveWithCaregiver: livesWithCaregiver };
};

/**
 * Pre-fill the shared frequent-locations state by aggregating each
 * dependant's `other` residences into one deduped list. The same `Residence`
 * id can appear under multiple dependants (family-shared rows); the Map
 * collapses those into a single entry.
 */
const hydrateFrequentLocations = (dependantResidences: readonly IDependantResidences[]): IFrequentLocationsState => {
  const byId = new Map<string, IOnboardingResidence>();
  for (const { other } of dependantResidences) {
    for (const r of other) {
      if (!byId.has(r.id)) {
        byId.set(r.id, r);
      }
    }
  }
  if (byId.size === 0) {
    return INITIAL_FREQUENT_LOCATIONS_STATE;
  }
  return {
    toggle: true,
    places: [...byId.values()].map((r) => ({
      id: r.id,
      text: r.formatted ?? r.addressLine1 ?? r.city,
      address: residenceToAddress(r),
    })),
  };
};

// ---------------------------------------------------------------------------
// Frequent-locations sub-form — array of free-text address inputs with
// add/remove. First row always renders (no remove button); subsequent rows
// get a small ✕ next to them.
// ---------------------------------------------------------------------------

interface IFrequentLocationsFormProps {
  places: IFrequentPlace[];
  onChange: (next: IFrequentPlace[]) => void;
  apiKey: string;
}

const FrequentLocationsForm = ({ places, onChange, apiKey }: IFrequentLocationsFormProps) => {
  const setRow = (i: number, patch: Partial<IFrequentPlace>) => {
    const next = [...places];
    next[i] = { ...next[i], ...patch };
    onChange(next);
  };
  // Editing after a pick invalidates the structured selection until the user
  // chooses another suggestion — frequent locations require a real Places pick.
  const onText = (i: number, text: string) =>
    setRow(i, { text, address: places[i].address?.formatted === text ? places[i].address : null });
  const onPick = (i: number, parsed: IParsedAddress) =>
    setRow(i, { text: parsed.formattedAddress, address: toResidenceAddress(parsed) });
  const remove = (i: number) => onChange(places.filter((_, idx) => idx !== i));
  const add = () => onChange([...places, { id: null, text: '', address: null }]);

  return (
    <div className="border-secondary flex flex-col gap-3 rounded-xl border p-3">
      {places.map((place, i) => {
        // A picked row that came back without a city can't be saved — surface
        // why inline rather than leaving Continue mysteriously disabled.
        const needsCity = place.address !== null && !isCompleteAddress(place.address);
        return (
          // The id is stable for existing rows (use it); new rows fall back to
          // the index since they have no upstream identity yet.
          <div key={place.id ?? `new-${i}`} className="flex flex-col gap-1">
            <div className="flex items-end gap-2">
              <div className="flex-1">
                <AddressAutocomplete
                  apiKey={apiKey}
                  label={i === 0 ? 'Location' : undefined}
                  placeholder="City, State"
                  value={place.text}
                  // City- or street-level so a frequent location can be a bare
                  // city or a precise address — both types always resolve a
                  // city, keeping city-less regions ("Lake Tahoe") out.
                  includedPrimaryTypes={['street_address', 'locality']}
                  onInputChange={(v) => onText(i, v)}
                  onSelect={(parsed) => onPick(i, parsed)}
                />
              </div>
              {i > 0 && (
                <button
                  type="button"
                  onClick={() => remove(i)}
                  aria-label="Remove location"
                  className="text-fg-quaternary hover:text-fg-secondary mb-1 p-2"
                >
                  <CloseIcon className="size-4" />
                </button>
              )}
            </div>
            {needsCity && (
              <p className="text-error-primary text-sm font-medium">
                Pick a location with a city, like a town or neighborhood.
              </p>
            )}
          </div>
        );
      })}
      <button type="button" onClick={add} className="text-brand-primary self-start text-sm font-semibold">
        + Add another location
      </button>
    </div>
  );
};
FrequentLocationsForm.displayName = 'FrequentLocationsForm';

// ---------------------------------------------------------------------------
// Residence form fields — drives a single `IResidenceFormState` (the "live
// with you" answer + the conditionally-revealed primary-residence fields).
// Re-used by Path A (one form for the household) and Path B (one per parent
// card). The frequent-locations toggle + list lives ONE level up — it's
// always a single shared question even in Path B, so we don't want it inside
// the per-parent card.
// ---------------------------------------------------------------------------

interface IResidenceFormFieldsProps {
  state: IResidenceFormState;
  onChange: (next: IResidenceFormState) => void;
  liveWithCaregiverLabel: string;
  addressLabel: string;
  apiKey: string;
}

const ResidenceFormFields = ({
  state,
  onChange,
  liveWithCaregiverLabel,
  addressLabel,
  apiKey,
}: IResidenceFormFieldsProps) => {
  const update = <K extends keyof IResidenceFormState>(field: K, value: IResidenceFormState[K]) => {
    onChange({ ...state, [field]: value });
  };

  const showAddressFields = state.liveWithCaregiver === false;
  const showOwnership = showAddressFields && state.residenceType === ResidenceType.IndependentLiving;

  return (
    <div className="flex w-full flex-col gap-6">
      <div className="flex w-full flex-col gap-1.5">
        <Label className="text-secondary text-sm font-bold">{liveWithCaregiverLabel}</Label>
        <YesNoToggle
          value={state.liveWithCaregiver}
          onChange={(v) => update('liveWithCaregiver', v)}
          ariaLabel={liveWithCaregiverLabel}
        />
      </div>

      {showAddressFields && (
        <>
          <AddressAutocomplete
            apiKey={apiKey}
            label={addressLabel}
            placeholder="123 Main St. San Francisco, CA"
            value={state.addressText}
            // Street-level only — a primary residence must be a full address,
            // so cities/regions never appear as suggestions.
            includedPrimaryTypes={['street_address']}
            onInputChange={(text) =>
              onChange({
                ...state,
                addressText: text,
                address: state.address?.formatted === text ? state.address : null,
              })
            }
            onSelect={(parsed) =>
              onChange({ ...state, addressText: parsed.formattedAddress, address: toResidenceAddress(parsed) })
            }
          />

          <Select
            label="Living situation"
            selectedKey={state.residenceType}
            onSelectionChange={(key) => update('residenceType', key as ResidenceType)}
            items={RESIDENCE_TYPE_OPTIONS}
          >
            {(item: SelectItemType) => <SelectItem id={item.id} label={item.label} />}
          </Select>

          {state.residenceType === ResidenceType.Other && (
            <Input
              label="Describe their living situation"
              placeholder="e.g. Temporary rehabilitation facility"
              value={state.residenceTypeOther}
              onChange={(v) => update('residenceTypeOther', v)}
            />
          )}

          {showOwnership && (
            <>
              <Select
                label="Home ownership"
                placeholder="Select"
                selectedKey={state.homeOwnership ?? undefined}
                onSelectionChange={(key) => update('homeOwnership', key as HousingOwnership)}
                items={HOME_OWNERSHIP_OPTIONS}
              >
                {(item: SelectItemType) => <SelectItem id={item.id} label={item.label} />}
              </Select>

              {state.homeOwnership === HousingOwnership.Other && (
                <Input
                  label="Describe the ownership situation"
                  placeholder="e.g. Co-owned with sibling"
                  value={state.homeOwnershipOther}
                  onChange={(v) => update('homeOwnershipOther', v)}
                />
              )}
            </>
          )}
        </>
      )}
    </div>
  );
};
ResidenceFormFields.displayName = 'ResidenceFormFields';

// ---------------------------------------------------------------------------
// Path B parent card — wraps a `ResidenceFormFields` with a header showing
// whose home it is ("Jane's home"). Only used when `liveTogether === false`.
// ---------------------------------------------------------------------------

interface IParentCardProps {
  title: string;
  children: React.ReactNode;
}

const ParentCard = ({ title, children }: IParentCardProps) => {
  return (
    <div className="border-secondary flex w-full flex-col gap-4 rounded-xl border p-4">
      <p className="text-primary text-md font-semibold">{title}</p>
      {children}
    </div>
  );
};
ParentCard.displayName = 'ParentCard';

// ---------------------------------------------------------------------------
// Submit — translate form state into 0..N `upsertResidence` calls. Spec:
//
//   - "Live with you = Yes" → no new residence (caregiver's existing address
//     covers them too). Frequent places (if any) are still created.
//   - "Live with you = No" → one primary `Residence` per parent group.
//   - "Frequently away = Yes" → one non-primary `Residence` per typed
//     location, family-shared (no owner) since the question is asked once
//     for the whole household.
//
// Path A (liveTogether=true or single dependant): the primary residence is
// owned by the first dependant (the DB schema only allows at-most-one of
// userId/dependantId per row, so we can't co-own a residence between two
// dependants — see ORN-868 for the polymorphic owner upgrade).
//
// Path B (liveTogether=false): each parent's form drives its own primary,
// owned by their respective dependant.
//
// Frequent-location rows are appended separately via
// `buildFrequentPlaceInputs` since the form asks that question once for the
// whole household, not per-parent.
// ---------------------------------------------------------------------------

interface IBuildInputsArgs {
  pathBForms?: { dependantId: string; form: IResidenceFormState }[];
  pathAForm?: { dependantId: string; form: IResidenceFormState };
}

export const buildResidenceInputs = ({ pathAForm, pathBForms }: IBuildInputsArgs): UpsertResidenceInput[] => {
  const groups = pathAForm ? [pathAForm] : (pathBForms ?? []);
  const inputs: UpsertResidenceInput[] = [];

  for (const { dependantId, form } of groups) {
    if (form.liveWithCaregiver === false && form.address !== null) {
      const addr = form.address;
      // residenceId set when the user is editing an already-saved primary
      // residence — the mutation updates that row in place. When null, a
      // new Residence is created. Ownership pointers (userId / dependantId)
      // are create-only on the wire, so they're ignored in the update path.
      inputs.push({
        residenceId: form.primaryResidenceId,
        dependantId,
        userId: null,
        isPrimary: true,
        residenceType: form.residenceType,
        residenceTypeOther: form.residenceType === ResidenceType.Other ? form.residenceTypeOther.trim() || null : null,
        addressLine1: addr.addressLine1,
        addressLine2: addr.addressLine2,
        city: addr.city,
        state: addr.state,
        postalCode: addr.postalCode,
        country: addr.country,
        placeId: addr.placeId,
        formatted: addr.formatted,
        ownership: form.residenceType === ResidenceType.IndependentLiving ? form.homeOwnership : null,
        ownershipOther: form.homeOwnership === HousingOwnership.Other ? form.homeOwnershipOther.trim() || null : null,
      });
    }
  }

  return inputs;
};

/**
 * Translate the shared frequent-locations state into 0..N non-primary
 * `Residence` inputs. Frequent locations are always family-shared (owner
 * null) — the question is asked once across the household, so the rows are
 * never pinned to an individual dependant.
 *
 * Returns `[]` when the toggle is anything other than `true`, even if the
 * user typed into a row before flipping back.
 */
export const buildFrequentPlaceInputs = (state: IFrequentLocationsState): UpsertResidenceInput[] => {
  if (state.toggle !== true) {
    return [];
  }
  const inputs: UpsertResidenceInput[] = [];
  for (const place of state.places) {
    const addr = place.address;
    // Defensive: `isFrequentLocationsReady` already blocks Continue on a
    // city-less pick, so an incomplete row here means a code path slipped
    // past the gate — never emit an empty `city` to the wire regardless.
    if (!isCompleteAddress(addr)) {
      continue;
    }
    inputs.push({
      residenceId: place.id,
      dependantId: null,
      userId: null,
      isPrimary: false,
      residenceType: ResidenceType.Other,
      residenceTypeOther: 'Frequent location',
      addressLine1: addr.addressLine1,
      addressLine2: addr.addressLine2,
      city: addr.city,
      state: addr.state,
      postalCode: addr.postalCode,
      country: addr.country,
      placeId: addr.placeId,
      formatted: addr.formatted,
      ownership: null,
      ownershipOther: null,
    });
  }
  return inputs;
};

// ---------------------------------------------------------------------------
// Validation — every required answer must be picked before Continue enables.
// ---------------------------------------------------------------------------

export const isFormReady = (state: IResidenceFormState): boolean => {
  if (state.liveWithCaregiver === null) {
    return false;
  }
  if (state.liveWithCaregiver === false) {
    // A primary residence must be a full street address — `addressLine1` is
    // only populated for street-level Places picks (or a hydrated row) — and
    // must carry a city/country like any other residence.
    if (!isCompleteAddress(state.address) || state.address?.addressLine1 == null) {
      return false;
    }
    if (state.residenceType === ResidenceType.Other && state.residenceTypeOther.trim().length === 0) {
      return false;
    }
    if (state.residenceType === ResidenceType.IndependentLiving) {
      if (state.homeOwnership === null) {
        return false;
      }
      if (state.homeOwnership === HousingOwnership.Other && state.homeOwnershipOther.trim().length === 0) {
        return false;
      }
    }
  }
  return true;
};

export const isFrequentLocationsReady = (state: IFrequentLocationsState): boolean => {
  if (state.toggle === null) {
    return false;
  }
  if (state.toggle === true) {
    // At least one real pick, and every pick must resolve to a city/country —
    // a row the user picked but that came back city-less blocks Continue so it
    // can't be silently dropped at submit (see `isCompleteAddress`).
    const picked = state.places.filter((p) => p.address !== null);
    return picked.length > 0 && picked.every((p) => isCompleteAddress(p.address));
  }
  return true;
};

// ---------------------------------------------------------------------------
// Step component
// ---------------------------------------------------------------------------

/**
 * "Living situation" screen — captures residence details for the user's
 * loved-one(s) with significant branching driven by who lives with whom.
 *
 * **Path A** (single dependant, OR partnership living together) — one shared
 * form: "live with you?" → conditional address/living-situation/ownership
 * fields → "frequently away?" → conditional multi-location list.
 *
 * **Path B** (partnership living apart) — one card per parent, each running
 * the same sub-form independently.
 *
 * Persistence on Continue creates 0..N residences:
 *   - 1 primary per parent group whose "live with you" answer is No
 *   - 1 non-primary per typed "frequent location"
 *
 * "Live with you = Yes" intentionally skips primary-residence creation — the
 * caregiver's address from the earlier About-You screen is implicit.
 *
 * "Skip for now" bypasses persistence entirely; the wizard is forward-only
 * at this slot so a skipped step doesn't trap the user.
 *
 * Figma: Mantle - UX/UI, node 4174:70736 "Onboarding Step 11".
 */
const DependantResidenceStep = ({ onNext, onBack }: IOnboardingStepProps) => {
  const { dependants, partnershipExists, partnership } = useOnboardingDependants();
  const { dependantResidences, refetch: refetchDependantResidences } = useOnboardingDependantResidences();
  const [upsertResidence] = useUpsertResidence();
  const [deleteResidence] = useDeleteResidence();
  const [updateCaregiverRelationship] = useUpdateCaregiverRelationship();
  const [updateDependantPartnership] = useUpdateDependantPartnership();
  // Empty key falls back to the typeahead's "Could not load suggestions"
  // empty state so Storybook / key-less local dev still render.
  const apiKey = getOptionalEnv('VITE_GOOGLE_PLACES_API_KEY') ?? '';

  const [liveTogether, setLiveTogether] = React.useState<boolean | null>(null);
  const [parentAForm, setParentAForm] = React.useState<IResidenceFormState>(INITIAL_FORM_STATE);
  const [parentBForm, setParentBForm] = React.useState<IResidenceFormState>(INITIAL_FORM_STATE);
  const [frequentLocations, setFrequentLocations] = React.useState<IFrequentLocationsState>(
    INITIAL_FREQUENT_LOCATIONS_STATE
  );
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [submitError, setSubmitError] = React.useState(false);
  // One-shot hydration guard. We pre-fill from the residence query exactly
  // once per dependant set; subsequent re-fetches (e.g. an in-flight upsert's
  // refetch) must not stomp the user's in-progress edits.
  const hasHydratedRef = React.useRef(false);
  // Snapshot of every `Residence.id` we hydrated this session (primary +
  // frequent). On submit we diff this against the ids the user kept (those
  // still upserting) and `deleteResidence`-everything-else. Captures the
  // orphan cases the per-row removal tracker missed: live-with-you flipped
  // No→Yes, Path A→B switch, frequent-locations toggle flipped Yes→No,
  // address cleared, etc.
  const hydratedResidenceIdsRef = React.useRef<ReadonlySet<string>>(new Set());

  const isPartnership = partnershipExists && dependants.length >= 2;

  // Hydrate the form state from existing residences the first time both
  // queries have resolved. Guarded by `hasHydratedRef` so subsequent
  // residence-list updates don't overwrite in-progress edits.
  React.useEffect(() => {
    if (hasHydratedRef.current || dependants.length === 0 || dependantResidences.length === 0) {
      return;
    }
    const residencesById = new Map(dependantResidences.map((d) => [d.dependantId, d]));
    const dependantA = dependants[0];
    const dependantB = dependants[1] ?? null;
    const aResidences = residencesById.get(dependantA.id) ?? null;
    const bResidences = dependantB !== null ? (residencesById.get(dependantB.id) ?? null) : null;

    setParentAForm(hydrateFormState(dependantA.id, dependantA.livesWithCaregiver, aResidences));
    if (dependantB !== null) {
      setParentBForm(hydrateFormState(dependantB.id, dependantB.livesWithCaregiver, bResidences));
      setLiveTogether(partnership?.liveTogether ?? null);
    }
    setFrequentLocations(hydrateFrequentLocations(dependantResidences));

    // Snapshot the `Residence.id`s this step is responsible for, so the
    // submit-time diff can delete the ones the user dropped. Exclude
    // caregiver-owned (`user`) residences: a dependant's resolved `primary`
    // can be the caregiver's home (via `lives_with_caregiver`), and that row
    // must never be deleted by this step.
    const hydratedIds = new Set<string>();
    for (const { primary, other } of dependantResidences) {
      if (primary !== null && primary.ownerKind !== 'user') {
        hydratedIds.add(primary.id);
      }
      for (const r of other) {
        if (r.ownerKind !== 'user') {
          hydratedIds.add(r.id);
        }
      }
    }
    hydratedResidenceIdsRef.current = hydratedIds;

    hasHydratedRef.current = true;
  }, [dependants, dependantResidences, partnership]);

  // Path B is the only "branch" — partnership with `liveTogether === false`
  // splits into two per-parent cards. Everything else falls through to Path
  // A's single shared form, including the partnership-pristine state where
  // `liveTogether` hasn't been answered yet. That way all three top-level
  // toggles (live together / live with you / frequently away) are visible
  // upfront — flipping the live-together answer just controls whether the
  // address-and-situation fields render once (Path A) or twice (Path B).
  const isPathB = isPartnership && liveTogether === false;
  const isPathA = !isPathB;

  const canSubmit = (() => {
    if (isSubmitting) {
      return false;
    }
    if (isPartnership && liveTogether === null) {
      return false;
    }
    if (!isFrequentLocationsReady(frequentLocations)) {
      return false;
    }
    if (isPathA) {
      return isFormReady(parentAForm);
    }
    if (isPathB) {
      return isFormReady(parentAForm) && isFormReady(parentBForm);
    }
    return false;
  })();

  const handleContinue = React.useCallback(async () => {
    if (!canSubmit) {
      return;
    }
    const dependantA = dependants[0];
    const dependantB = dependants[1] ?? null;
    const primaryInputs = isPathB
      ? buildResidenceInputs({
          pathBForms: [
            { dependantId: dependantA.id, form: parentAForm },
            ...(dependantB !== null ? [{ dependantId: dependantB.id, form: parentBForm }] : []),
          ],
        })
      : buildResidenceInputs({
          pathAForm: { dependantId: dependantA.id, form: parentAForm },
        });
    const frequentInputs = buildFrequentPlaceInputs(frequentLocations);
    const inputs = [...primaryInputs, ...frequentInputs];

    // True diff: any id we hydrated but aren't upserting (skipped because
    // the toggle flipped, the address cleared, the user removed the row,
    // the path switched, etc.) → delete it.
    const keptIds = new Set<string>();
    for (const input of inputs) {
      if (input.residenceId !== null && input.residenceId !== undefined) {
        keptIds.add(input.residenceId);
      }
    }
    const orphanIds: string[] = [];
    for (const id of hydratedResidenceIdsRef.current) {
      if (!keptIds.has(id)) {
        orphanIds.push(id);
      }
    }

    // Persist the toggle answers onto the relationship rows the review step
    // already created. Path A's single form answers for the whole household,
    // so its `liveWithCaregiver` applies to every dependant's caregiver edge;
    // Path B answers per parent. `liveTogether` lives on the partnership row.
    const caregiverRelUpdates = (
      isPathB
        ? [
            {
              caregiverRelationshipId: dependantA.caregiverRelationshipId,
              livesWithCaregiver: parentAForm.liveWithCaregiver,
            },
            ...(dependantB !== null
              ? [
                  {
                    caregiverRelationshipId: dependantB.caregiverRelationshipId,
                    livesWithCaregiver: parentBForm.liveWithCaregiver,
                  },
                ]
              : []),
          ]
        : dependants.map((d) => ({
            caregiverRelationshipId: d.caregiverRelationshipId,
            livesWithCaregiver: parentAForm.liveWithCaregiver,
          }))
    ).filter(
      (u): u is { caregiverRelationshipId: string; livesWithCaregiver: boolean } => u.livesWithCaregiver !== null
    );

    setIsSubmitting(true);
    setSubmitError(false);
    try {
      // `allSettled`, not `all`: each upsert commits independently server-side,
      // so one rejection must not strand the rows that succeeded. We always
      // refetch (so the cache reflects partial success) and only advance when
      // every operation succeeded. A retry is safe — the server routes a
      // primary re-create to an update, so it can't trip the per-owner index.
      const results = await Promise.allSettled([
        ...inputs.map((input) => upsertResidence({ variables: { input } })),
        ...orphanIds.map((residenceId) => deleteResidence({ variables: { input: { residenceId } } })),
        ...caregiverRelUpdates.map((input) => updateCaregiverRelationship({ variables: { input } })),
        ...(isPartnership && partnership !== null && liveTogether !== null
          ? [
              updateDependantPartnership({
                variables: { input: { dependantPartnershipId: partnership.id, liveTogether } },
              }),
            ]
          : []),
      ]);
      // Refresh the server-derived residence fields into the cache, so
      // navigating back to this step re-hydrates from fresh data rather than
      // the pre-mutation snapshot — even when some operations failed.
      await refetchDependantResidences();

      const failures = results.filter((r): r is PromiseRejectedResult => r.status === 'rejected');
      if (failures.length > 0) {
        failures.forEach((f) => logger.error('Failed to reconcile residences', f.reason));
        setSubmitError(true);
        return;
      }
      onNext();
    } catch (error) {
      logger.error('Failed to reconcile residences', error);
      setSubmitError(true);
    } finally {
      setIsSubmitting(false);
    }
  }, [
    canSubmit,
    dependants,
    isPathB,
    isPartnership,
    partnership,
    liveTogether,
    parentAForm,
    parentBForm,
    frequentLocations,
    upsertResidence,
    deleteResidence,
    updateCaregiverRelationship,
    updateDependantPartnership,
    refetchDependantResidences,
    onNext,
  ]);

  // Wizard routing guarantees the user passed through DEPENDANT_COUNT /
  // ENTRY / REVIEW first, so Apollo's cache is warm here. Returning null
  // covers the empty render frame before `useOnboardingDependants` resolves
  // on a cold load and keeps `getDependantNames` from ever seeing `[]`.
  if (dependants.length === 0) {
    return null;
  }

  const dependantA = dependants[0];
  const dependantB = dependants[1] ?? null;
  const allDependantsName = getDependantNames(dependants);
  const aName = getDisplayName(dependantA);
  const bName = dependantB !== null ? getDisplayName(dependantB) : '';

  // Only Q1 (live together?) names both partners — Q2 and Q3 use "they" /
  // "Does X" to avoid the repetitive "Jane and Peter / Jane and Peter / Jane
  // and Peter" wall when both are spelled out three times in a row.
  const liveTogetherLabel = `Do ${allDependantsName} live together?`;
  const pathALiveWithCaregiverLabel =
    dependants.length === 1 ? `Does ${aName} live with you?` : 'Do they live with you?';
  // Always asked once across the household — Path B doesn't re-ask per
  // parent. Pronoun matches the same rule as Q2.
  const awayFromHomeLabel =
    dependants.length === 1
      ? `Does ${aName} frequently spend time in a location away from home?`
      : 'Do they frequently spend time in a location away from home?';

  return (
    <>
      <OnboardingTitle title="Living situation" onBack={onBack} />

      <div className="flex w-full flex-col gap-6">
        {isPartnership && (
          <div className="flex w-full flex-col gap-1.5">
            <Label className="text-secondary text-sm font-bold">{liveTogetherLabel}</Label>
            <YesNoToggle value={liveTogether} onChange={setLiveTogether} ariaLabel={liveTogetherLabel} />
          </div>
        )}

        {isPathA && (
          <ResidenceFormFields
            state={parentAForm}
            onChange={setParentAForm}
            liveWithCaregiverLabel={pathALiveWithCaregiverLabel}
            addressLabel={`${getDependantPossessive(dependants, "your loved one's")} address`}
            apiKey={apiKey}
          />
        )}

        {isPathB && (
          <>
            <ParentCard title={`${aName}'s home`}>
              <ResidenceFormFields
                state={parentAForm}
                onChange={setParentAForm}
                liveWithCaregiverLabel={`Does ${aName} live with you?`}
                addressLabel={`${aName}'s address`}
                apiKey={apiKey}
              />
            </ParentCard>
            {dependantB !== null && (
              <ParentCard title={`${bName}'s home`}>
                <ResidenceFormFields
                  state={parentBForm}
                  onChange={setParentBForm}
                  liveWithCaregiverLabel={`Does ${bName} live with you?`}
                  addressLabel={`${bName}'s address`}
                  apiKey={apiKey}
                />
              </ParentCard>
            )}
          </>
        )}

        <div className="flex w-full flex-col gap-1.5">
          <Label className="text-secondary text-sm font-bold">{awayFromHomeLabel}</Label>
          <YesNoToggle
            value={frequentLocations.toggle}
            onChange={(v) => setFrequentLocations({ ...frequentLocations, toggle: v })}
            ariaLabel={awayFromHomeLabel}
          />
        </div>

        {frequentLocations.toggle === true && (
          <FrequentLocationsForm
            places={frequentLocations.places}
            onChange={(next) => setFrequentLocations({ ...frequentLocations, places: next })}
            apiKey={apiKey}
          />
        )}
      </div>

      <div className="flex w-full flex-col items-center gap-5">
        {submitError && (
          <p className="text-error-primary text-sm font-medium">
            Something went wrong saving your answers. Please try again.
          </p>
        )}
        <OnboardingStepActions
          isContinueDisabled={!canSubmit}
          isContinueLoading={isSubmitting}
          onContinue={() => void handleContinue()}
          onSkip={onNext}
        />
      </div>
    </>
  );
};

DependantResidenceStep.displayName = 'DependantResidenceStep';

export default DependantResidenceStep;
